import some from 'lodash/some';
import { connect } from 'react-redux';
import {
    CheckoutLifecycleEventPostMessageName,
    checkoutPostMessage,
} from '@eventbrite/checkout-postmessage-api';
import { joinPath } from '@eventbrite/path-utils';
import { navigateToTicketSelection as navigateToTicketSelectionAction } from '../actions/initialize';
import { notifyParentWidgetModalClose as notifyParentWidgetModalCloseAction } from '../actions/notifyParent';
import {
    abandonOrderCloseAttemptWithStatusPath as abandonOrderCloseAttemptWithStatusPathAction,
    abandonOrderOnClose as abandonOrderOnCloseAction,
} from '../actions/order/common';
import { queueRenew as queueRenewAction } from '../actions/waitingRoom';
import {
    BASE_URL,
    CHECKOUT_PATH,
    CHECKOUT_TITLE,
    DELIVERY_METHODS_PATH,
    EXTERNAL_PAYMENT_PATH,
    LOGIN_PATH,
    ORDER_CONFIRMATION_TITLE,
    PICK_A_SEAT_PATH,
    PURCHASE_CONFIRMATION_PATH,
    RESERVED_TICKETS_CONFIRMATION_PATH,
    STATUS_PATH,
    TICKETS_FULL_SIZE_PATH,
} from '../constants';
import {
    getIsTicketSelectionCompactSizeFlow,
    isNativeApp,
    getIsProd,
    getIsAppRebrandActive,
} from '../selectors/app';
import { getIsOnlyCheckoutFlow } from '../selectors/app/getIsOnlyCheckoutFlow';
import { isSeriesInfiniteCalendar } from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { getIsTransferredOrder } from '../selectors/order';
import { isAddAttendeesOrder } from '../selectors/order/typedOrderSelectors';
import {
    getRoutePathname,
    isOrganizerQuestionsPage,
    isTicketsCompactPage,
    isOnlyCheckoutPage,
    getIsOrderSuccessAnimationPage,
} from '../selectors/routing';
import { shouldPerformTablessCheckout } from '../utils/browser';
import { ABANDON_ORDER_ATTEMPT_STATUSES } from './status/constants';
import { AbandonOrderCloseAttemptParams } from '../utils/routes';
import { ConfirmStatus } from '../constants/confirmStatus';
import { shouldNavigateToTicketSelectionWhenClosingModal } from '../selectors/app';
import { getLoginFromCheckoutPage } from '../selectors/user';

// The names of routes which should show modal to warn users when clicking on "close" on checkout widget
const ROUTES_TO_SHOW_ABANDON_ORDER_PAGE_ON_CLOSE = [
    CHECKOUT_PATH,
    DELIVERY_METHODS_PATH,
    EXTERNAL_PAYMENT_PATH,
    PICK_A_SEAT_PATH,
    RESERVED_TICKETS_CONFIRMATION_PATH,
];

const _shouldShowAbandonOrderPageOnCloseForLoginPath = (router, state) =>
    router.getCurrentLocation().pathname === `${BASE_URL}/${LOGIN_PATH}` &&
    getLoginFromCheckoutPage(state);

const _shouldShowAbandonOrderPageOnClose = (router, state) =>
    some(ROUTES_TO_SHOW_ABANDON_ORDER_PAGE_ON_CLOSE, (routeName) =>
        router.isActive(joinPath(BASE_URL, routeName)),
    ) || _shouldShowAbandonOrderPageOnCloseForLoginPath(router, state);

const _getModalAriaLabel = (state, ownProps) => {
    const { router } = ownProps;
    const currentLocation = router.getCurrentLocation().pathname;

    switch (currentLocation) {
        case joinPath(BASE_URL, CHECKOUT_PATH):
            return CHECKOUT_TITLE;
        case joinPath(BASE_URL, PURCHASE_CONFIRMATION_PATH):
            return ORDER_CONFIRMATION_TITLE;
        default:
            return `${CHECKOUT_TITLE} ${state.event.name.text}`;
    }
};

export const _mapStateToProps = (state, ownProps) => ({
    isAddAttendeesOrder: isAddAttendeesOrder(state),
    isAppRebrandActive: getIsAppRebrandActive(state),
    isModal: state.app.isModal,
    isNativeApp: isNativeApp(state),
    isOnlyCheckoutFlow: getIsOnlyCheckoutFlow(state),
    isOnlyCheckoutPage: isOnlyCheckoutPage(state),
    isOrderSuccessAnimationPage: getIsOrderSuccessAnimationPage(state),
    isOrganizerQuestionsPage: isOrganizerQuestionsPage(state),
    isProductionEnv: getIsProd(state),
    isTicketSelectionCompactSizeFlow:
        getIsTicketSelectionCompactSizeFlow(state),
    isTicketsCompactPage: isTicketsCompactPage(state),
    isTransferredOrder: getIsTransferredOrder(state),
    modalAriaLabel: _getModalAriaLabel(state, ownProps),
    performTabless: shouldPerformTablessCheckout(),
    routePathname: getRoutePathname(state),
    themeSettings: state.app.themeSettings,
    isSeriesInfiniteCalendar: isSeriesInfiniteCalendar(state),
});

const getAbandonOrderCloseAttemptParams = (router, state) => {
    const showAbandonOrderPage = _shouldShowAbandonOrderPageOnClose(
        router,
        state,
    );
    if (!showAbandonOrderPage) {
        return null;
    }

    const { isActive } = router;

    const [, paramsForCurrentPath] = Object.entries(
        AbandonOrderCloseAttemptParams,
    ).find(([path]) => isActive(joinPath(BASE_URL, path))) ?? [
        null,
        {
            statusPath: ConfirmStatus.close,
            pathName: CHECKOUT_PATH,
            fullPathName: router.location?.pathname,
        },
    ];

    return paramsForCurrentPath;
};

export const handleCloseAttempt = (router) => (dispatch, getState) => {
    const state = getState();
    const shouldNavigateToTicketSelection =
        shouldNavigateToTicketSelectionWhenClosingModal(state);
    const abandonOrderCloseAttemptParams = getAbandonOrderCloseAttemptParams(
        router,
        state,
    );

    if (abandonOrderCloseAttemptParams) {
        // If the user clicks the modal close button on the checkout page, delivery method page, or
        // reserved confirmation screen, we want to confirm that they really want to abandon their order
        // before closing the modal
        const { statusPath, pathName, fullPathName } =
            abandonOrderCloseAttemptParams;
        dispatch(
            abandonOrderCloseAttemptWithStatusPathAction({
                pathName,
                statusPath,
                fullPathName,
            }),
        );
    } else {
        if (!router.isActive(joinPath(BASE_URL, PURCHASE_CONFIRMATION_PATH))) {
            checkoutPostMessage({
                messageName:
                    CheckoutLifecycleEventPostMessageName.orderStatusCanceled,
            });
        }

        if (shouldNavigateToTicketSelection) {
            dispatch(navigateToTicketSelectionAction());
        } else {
            dispatch(
                notifyParentWidgetModalCloseAction(TICKETS_FULL_SIZE_PATH),
            );
        }
    }
};

export const getIsAbandonOrderAttemptPage = (router) => {
    const { isActive: isActiveRoute } = router;
    const isAbandonOrderAttemptPage = some(
        ABANDON_ORDER_ATTEMPT_STATUSES,
        (status) => isActiveRoute(joinPath(BASE_URL, STATUS_PATH, status)),
    );

    return isAbandonOrderAttemptPage;
};

export const getOnCloseFn = (router, isAbandonOrderAttemptPage, dispatch) =>
    !isAbandonOrderAttemptPage
        ? () => {
              dispatch(handleCloseAttempt(router));
          }
        : null;

export const _mapDispatchToProps = (dispatch, ownProps) => {
    const { router } = ownProps;
    const { isActive: isActiveRoute } = router;

    const onQueueRenew = () => dispatch(queueRenewAction());
    const abandonOrderOnClose = () => {
        if (isActiveRoute(joinPath(BASE_URL, CHECKOUT_PATH))) {
            return dispatch(abandonOrderOnCloseAction());
        }
    };

    const isAbandonOrderAttemptPage = getIsAbandonOrderAttemptPage(router);

    return {
        abandonOrderOnClose,
        onQueueRenew,
        onClose: getOnCloseFn(router, isAbandonOrderAttemptPage, dispatch),
    };
};

export const connectPage = (ComponentClass) =>
    connect(_mapStateToProps, _mapDispatchToProps)(ComponentClass);
